import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  timeout: 15000,
});

// ─── Auth ────────────────────────────────────────────────────────────────────
export const getMe = () => api.get('/auth/me');
export const markTutorialDone = () => api.post('/auth/tutorial-done');
export const updateSettings = (settings) => api.put('/auth/settings', { settings });

// ─── Inventory ───────────────────────────────────────────────────────────────
export const getInventory = () => api.get('/inventory');
export const getAllInventories = () => api.get('/inventory/all');
export const getSectorInventory = (sectorId) => api.get(`/inventory/${sectorId}`);
export const collectFromSector = (sectorId) => api.post(`/inventory/${sectorId}/collect`);

// ─── Missions ────────────────────────────────────────────────────────────────
export const getMissionTemplates = () => api.get('/missions/templates');
export const getActiveMissions = () => api.get('/missions/active');
export const getMissionHistory = (page = 1) => api.get('/missions/history', { params: { page } });
export const startMission = (templateId, shipId, droneIds) =>
  api.post('/missions/start', { templateId, shipId, droneIds });
export const claimMission = (id) => api.post(`/missions/${id}/claim`);

// ─── Fleet ───────────────────────────────────────────────────────────────────
export const getFleet = () => api.get('/fleet');
export const getUpgradeCosts = (type, id) => api.get(`/fleet/upgrade-costs/${type}/${id}`);
export const upgradeShip = (shipId, module) => api.post('/fleet/upgrade-ship', { shipId, module });
export const upgradeDrone = (droneId, stat) => api.post('/fleet/upgrade-drone', { droneId, stat });
export const getBuildCosts = () => api.get('/fleet/build-costs');
export const buildShip = (type) => api.post('/fleet/build-ship', { type });
export const buildDrone = (type) => api.post('/fleet/build-drone', { type });
export const assignDrone = (droneId, shipId) => api.post('/fleet/assign-drone', { droneId, shipId });
export const deployDrone = (droneId, sectorId, resourceNodeId) =>
  api.post(`/fleet/drones/${droneId}/deploy`, { sectorId, resourceNodeId });
export const recallDrone = (droneId) => api.post(`/fleet/drones/${droneId}/recall`);
export const repairShip = (shipId) => api.post(`/fleet/ships/${shipId}/repair`);
export const commissionShip = (shipItemKey, sectorId, name) =>
  api.post('/fleet/commission-ship', { shipItemKey, sectorId, name });

// ─── Stations ────────────────────────────────────────────────────────────────
export const getStations = () => api.get('/stations');
export const getStationUpgradeCost = (type) => api.get(`/stations/upgrade-cost/${type}`);
export const upgradeStation = (type) => api.post('/stations/upgrade', { type });
export const collectStation = (type) => api.post('/stations/collect', { type });
export const getStationBuildCosts = () => api.get('/stations/build-costs');
export const buildStation = (type) => api.post('/stations/build', { type });
export const refineryConvert = (amount) => api.post('/stations/refinery-convert', { amount });

// ─── Market ──────────────────────────────────────────────────────────────────
export const getListings = (params) => api.get('/market/listings', { params });
export const getBaselinePrices = () => api.get('/market/baseline-prices');
export const baselineSell = (resourceType, quantity) =>
  api.post('/market/baseline-sell', { resourceType, quantity });
export const quickSell = (resourceType, quantity) =>
  api.post('/market/quick-sell', { resourceType, quantity });
export const createListing = (data) => api.post('/market/create-listing', data);
export const createBuyOrder = (data) => api.post('/market/create-buy-order', data);
export const buyListing = (listingId, quantity) => api.post('/market/buy', { listingId, quantity });
export const cancelListing = (listingId) => api.post('/market/cancel', { listingId });
export const getMyListings = () => api.get('/market/my-listings');
export const getTradeHistory = (page = 1) => api.get('/market/history', { params: { page } });

// ─── Auto-Sell ───────────────────────────────────────────────────────────────
export const getAutoSellRules = () => api.get('/market/auto-sell');
export const updateAutoSellRules = (rules) => api.put('/market/auto-sell', { rules });
export const deleteAutoSellRule = (resource) => api.delete(`/market/auto-sell/${resource}`);

// ─── Leaderboard ─────────────────────────────────────────────────────────────
export const getRankings = (metric, page = 1) =>
  api.get('/leaderboard', { params: { metric, page } });
export const getMyRanking = () => api.get('/leaderboard/me');

// ─── Admin ───────────────────────────────────────────────────────────────────
export const getAdminDashboard = () => api.get('/admin/dashboard');
export const getAdminPlayers = (params) => api.get('/admin/players', { params });
export const getAdminPlayerDetail = (id) => api.get(`/admin/players/${id}`);
export const banPlayer = (id, reason) => api.post(`/admin/players/${id}/ban`, { reason });
export const unbanPlayer = (id) => api.post(`/admin/players/${id}/unban`);
export const grantResources = (id, resources) => api.post(`/admin/players/${id}/grant`, { resources });
export const resetPlayer = (id) => api.post(`/admin/players/${id}/reset`);
export const getAdminGhosts = () => api.get('/admin/ghosts');
export const updateAdminGhost = (id, updates) => api.put(`/admin/ghosts/${id}`, updates);

// ─── Sectors ─────────────────────────────────────────────────────────────────
export const getSectors = () => api.get('/sectors');
export const getSector = (id) => api.get(`/sectors/${id}`);
export const getUndiscoveredSectors = () => api.get('/sectors/undiscovered');
export const claimSector = (id) => api.post(`/sectors/${id}/claim`);
export const scoutSector = (id) => api.post(`/sectors/${id}/scout`);

// ─── Production ──────────────────────────────────────────────────────────────
export const getFactories = (sectorId) => api.get('/production/factories', { params: sectorId ? { sectorId } : {} });
export const getProductionCosts = () => api.get('/production/costs');
export const getRecipes = (factoryType) => api.get(`/production/recipes/${factoryType}`);
export const buildFactory = (sectorId, factoryType) => api.post('/production/factories', { sectorId, factoryType });
export const setRecipe = (factoryId, recipeKey) => api.post(`/production/factories/${factoryId}/recipe`, { recipeKey });
export const startCraft = (factoryId, quantity) => api.post(`/production/factories/${factoryId}/craft`, { quantity });
export const cancelCraft = (jobId) => api.post(`/production/jobs/${jobId}/cancel`);
export const upgradeFactory = (factoryId) => api.post(`/production/factories/${factoryId}/upgrade`);

// ─── Logistics ───────────────────────────────────────────────────────────────
export const getTransportRoutes = () => api.get('/logistics/routes');
export const createTransportRoute = (sourceSectorId, destSectorId, resourceType, routeType) =>
  api.post('/logistics/routes', { sourceSectorId, destSectorId, resourceType, routeType });
export const deleteTransportRoute = (id) => api.delete(`/logistics/routes/${id}`);
export const toggleTransportRoute = (id, isActive) => api.post(`/logistics/routes/${id}/toggle`, { isActive });
export const assignTransportShip = (routeId, shipId) => api.post(`/logistics/routes/${routeId}/assign`, { shipId });
export const unassignTransportShip = (id) => api.post(`/logistics/transport-ships/${id}/unassign`);

// ─── Combat ──────────────────────────────────────────────────────────────────
export const getCombatFleets = () => api.get('/combat/fleets');
export const createCombatFleet = (name, sectorId) => api.post('/combat/fleets', { name, sectorId });
export const addShipToFleet = (fleetId, shipId) => api.post(`/combat/fleets/${fleetId}/add-ship`, { shipId });
export const removeShipFromFleet = (shipId) => api.post(`/combat/ships/${shipId}/remove-from-fleet`);
export const moveFleet = (fleetId, targetSectorId) => api.post(`/combat/fleets/${fleetId}/move`, { targetSectorId });
export const attackWithFleet = (fleetId) => api.post(`/combat/fleets/${fleetId}/attack`);
export const retreatFleet = (fleetId) => api.post(`/combat/fleets/${fleetId}/retreat`);
export const getBattles = () => api.get('/combat/battles');
export const getBattleDetail = (id) => api.get(`/combat/battles/${id}`);
export const getDefenses = (sectorId) => api.get(`/combat/defenses/${sectorId}`);
export const buildDefense = (sectorId, platformType) => api.post('/combat/defenses', { sectorId, platformType });

// ─── Corporations ────────────────────────────────────────────────────────────
export const getCorporations = () => api.get('/corporations');
export const getCorporation = (id) => api.get(`/corporations/${id}`);
export const createCorporation = (name, tag) => api.post('/corporations', { name, tag });
export const joinCorporation = (id) => api.post(`/corporations/${id}/join`);
export const leaveCorporation = () => api.post('/corporations/leave');
export const setCorporationRole = (targetUserId, role) => api.post('/corporations/set-role', { targetUserId, role });
export const declareWar = (targetCorpId) => api.post('/corporations/declare-war', { targetCorpId });
export const getCorporationWars = (corpId) => api.get(`/corporations/${corpId}/wars`);

// ─── Game Guide ──────────────────────────────────────────────────────────────
export const getGuideArticles = () => api.get('/guide');
export const getGuideArticle = (slug) => api.get(`/guide/${encodeURIComponent(slug)}`);
export const getAdminGuideArticles = () => api.get('/admin/guide');
export const createGuideArticle = (data) => api.post('/admin/guide', data);
export const updateGuideArticle = (id, data) => api.put(`/admin/guide/${id}`, data);
export const deleteGuideArticle = (id) => api.delete(`/admin/guide/${id}`);

export default api;
